import React, { useEffect, useState, useCallback } from 'react';
import { Star, Gift, Lock, TrendingUp, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import RewardCard from '../components/RewardCard';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_ORDER = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Legendary'];

const MetricCard = ({ label, value, sub, icon: Icon, testId }) => (
  <div
    className="rounded-lg border p-5 motion-fade-up"
    style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-3">
      <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint">{label}</p>
      {Icon && <Icon className="w-3.5 h-3.5 text-fg-muted" />}
    </div>
    <p className="text-3xl font-bold text-fg tracking-tight">{value}</p>
    {sub && <p className="text-[11px] text-fg-muted mt-1">{sub}</p>}
  </div>
);

const Rewards = () => {
  const { user, refreshUser } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);

  const fetchRewards = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/rewards`);
      setRewards(data);
    } catch { toast.error('Failed to load rewards'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRewards(); }, [fetchRewards]);

  const handleRedeem = async (reward) => {
    const balance = user?.super_points || 0;
    if (balance < reward.cost) {
      toast.error(`You need ${(reward.cost - balance).toLocaleString()} more ⭐ to redeem this`);
      return;
    }
    setRedeeming(reward.id);
    try {
      const { data } = await axios.post(`${API_URL}/api/rewards/${reward.id}/redeem`, {});
      toast.success(data.message);
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem reward');
    } finally { setRedeeming(null); }
  };

  if (loading) {
    return (
      <div className="min-h-screen"><Header />
        <div className="flex items-center justify-center py-20">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-transparent" style={{ borderTopColor: 'rgb(var(--accent))' }} />
        </div>
      </div>
    );
  }

  const superPointRewards = rewards.filter((r) => r.currency === 'super_points');
  const affiliateRewards = rewards.filter((r) => r.redemption_type === 'affiliate');
  const groupedByTier = TIER_ORDER.map(tier => ({
    tier,
    items: superPointRewards.filter((r) => r.tier === tier),
  })).filter(g => g.items.length > 0);

  const balance = user?.super_points || 0;
  const redeemableINR = Math.round(balance * 0.5);
  const totalRedeemed = user?.total_super_points_redeemed || 0;

  return (
    <div className="min-h-screen" data-testid="rewards-page">
      <Header />

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* Header */}
        <div className="mb-6 motion-fade-up">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-fg-faint font-medium mb-2">
            <Star className="w-3 h-3 fill-current" style={{ color: 'rgb(var(--accent))' }} />
            <span>Reward Wallet</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl font-bold tracking-tight mb-2 text-fg"
            data-testid="rewards-heading"
          >
            Cash out your Super Points
          </h1>
          <p className="text-sm text-fg-muted max-w-2xl">
            Super Points (⭐) are awarded to the top 3% of Pro performers each month. Redeem them for real gift cards.
          </p>
        </div>

        {/* Balance + Metrics row */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-8">

          {/* Big balance hero */}
          <div
            className="lg:col-span-2 relative overflow-hidden rounded-xl border p-6 motion-fade-up motion-delay-1"
            style={{
              background: 'linear-gradient(135deg, rgb(var(--card)) 0%, rgba(239,68,68,0.10) 100%)',
              borderColor: 'rgba(239,68,68,0.35)',
            }}
            data-testid="super-points-balance-card"
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl pointer-events-none"
              style={{ background: 'rgba(239,68,68,0.20)' }} />
            <div className="relative">
              <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-2">Your Balance</p>
              <div className="flex items-baseline gap-3 mb-1">
                <Star className="w-10 h-10 fill-current" style={{ color: 'rgb(var(--accent))' }} />
                <span className="text-6xl font-bold text-fg tracking-tight">{balance.toLocaleString('en-IN')}</span>
              </div>
              <p className="text-sm text-fg-muted">
                ≈ <span className="font-semibold text-fg">₹{redeemableINR.toLocaleString('en-IN')}</span> redeemable · 1 ⭐ = ₹0.50
              </p>
            </div>
          </div>

          <MetricCard
            label="Total Redeemed"
            value={totalRedeemed.toLocaleString('en-IN')}
            sub="Super points to date"
            icon={Gift}
            testId="metric-redeemed"
          />
          <MetricCard
            label="Redeemable Value"
            value={`₹${redeemableINR.toLocaleString('en-IN')}`}
            sub="At current rate"
            icon={TrendingUp}
            testId="metric-value"
          />
        </div>

        {/* No SP nudge */}
        {balance === 0 && (
          <div
            className="mb-8 p-5 rounded-lg border flex gap-3 motion-fade-up motion-delay-2"
            style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.35)' }}
            data-testid="no-super-points-banner"
          >
            <Lock className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'rgb(var(--warning))' }} />
            <div>
              <p className="text-sm font-semibold text-fg">How to earn Super Points</p>
              <p className="text-xs text-fg-muted mt-1">
                Subscribe to Pro, climb the monthly leaderboard, and finish in the top 3%. Top ranks earn up to <span className="font-semibold text-fg">15,000 ⭐ (₹7,500)</span> per month.
              </p>
            </div>
          </div>
        )}

        {/* Super Points Store by tier */}
        {groupedByTier.length > 0 && (
          <section className="mb-12" data-testid="super-points-store">
            <div className="flex items-center gap-2 mb-4 text-[11px] uppercase tracking-widest text-fg-faint font-medium">
              <Sparkles className="w-3 h-3" style={{ color: 'rgb(var(--accent))' }} />
              <span>Gift Card Store</span>
            </div>

            {groupedByTier.map(({ tier, items }) => (
              <div key={tier} className="mb-8" data-testid={`tier-section-${tier.toLowerCase()}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-bold uppercase tracking-widest text-fg-secondary">{tier} Tier</span>
                  <div className="flex-1 h-px" style={{ background: 'linear-gradient(to right, rgb(var(--border)), transparent)' }}></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((reward) => (
                    <RewardCard
                      key={reward.id}
                      reward={reward}
                      onRedeem={handleRedeem}
                      userSuperPoints={balance}
                      isRedeeming={redeeming === reward.id}
                    />
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Partner Offers */}
        {affiliateRewards.length > 0 && (
          <section data-testid="partner-offers">
            <div className="flex items-center gap-2 mb-4 text-[11px] uppercase tracking-widest text-fg-faint font-medium">
              <Gift className="w-3 h-3" style={{ color: 'rgb(16,185,129)' }} />
              <span>Partner Course Offers</span>
            </div>
            <p className="text-xs text-fg-muted mb-4">Free to browse — exclusive Aptivon discounts on top JEE/NEET prep</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {affiliateRewards.map((reward) => (
                <RewardCard
                  key={reward.id}
                  reward={reward}
                  onRedeem={handleRedeem}
                  userSuperPoints={balance}
                />
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default Rewards;
